/**
 * CastStripState — module-level singleton holding the local caster's
 * current cast-pipeline stage. Bridges the React side
 * (useCastLifecycleStream) to the Phaser side (NetobsCastStrip rendered
 * by PlayerRenderer per-frame).
 *
 * Why a singleton (not a ServiceRegistry entry, not a context, not an
 * event): there's exactly ONE local player. The state writer
 * (CastStripBridge) and reader (PlayerRenderer) live in different
 * worlds (React vs Phaser) but agree on a single shared module. No DI
 * ceremony needed.
 *
 * Stages (per docs/arcs/netcode-observability/02-PHASE-PLAN.md NO-4):
 *   0 = intent        — cast attempt fired
 *   1 = gate          — passed cooldown/mana/range/PvP/LoS gates
 *   2 = execute       — windup running / server received
 *   3 = wire          — server broadcast / projectile spawned
 *   4 = confirmed     — hit landed / status='completed'
 *
 * Visibility:
 *   - Active during in-flight + 1s after status moves out of in-flight
 *   - `read(now)` returns { visible, stage, rejected }
 *
 * Reset between casts: the bridge clears + replays as the lifecycle
 * stream's "newest in-flight group" changes.
 */

const FADE_AFTER_END_MS = 1000;
export const CAST_STRIP_STAGES = 5;

interface CastStripSnapshot {
  visible: boolean;
  stage: number;     // 0..CAST_STRIP_STAGES (inclusive). 0 = nothing lit, 5 = all lit.
  rejected: boolean;
}

class CastStripStateInner {
  private stage = 0;
  private rejected = false;
  private endedAt: number | null = null;
  private startedAt: number | null = null;

  /** Begin a new cast — clears prior state. */
  begin(): void {
    this.stage = 0;
    this.rejected = false;
    this.endedAt = null;
    this.startedAt = Date.now();
  }

  /** Advance to a specific stage (0..CAST_STRIP_STAGES). Monotonic — never
   *  regresses. Lifecycle events arrive in roughly-sequential order but
   *  rare reorderings shouldn't visually rewind. */
  advanceTo(stage: number): void {
    if (this.endedAt !== null) return;  // ignore late events post-end
    if (stage > this.stage) this.stage = Math.min(stage, CAST_STRIP_STAGES);
  }

  /** Mark the cast rejected — paints all segments red, schedules fade. */
  reject(): void {
    this.rejected = true;
    if (this.endedAt === null) this.endedAt = Date.now();
  }

  /** Cast completed normally — fills final segment + schedules fade. */
  complete(): void {
    this.stage = CAST_STRIP_STAGES;
    if (this.endedAt === null) this.endedAt = Date.now();
  }

  /** Force-clear — used by bridge teardown. */
  clear(): void {
    this.stage = 0;
    this.rejected = false;
    this.endedAt = null;
    this.startedAt = null;
  }

  /** Read current visual snapshot. Called per-frame by PlayerRenderer. */
  read(now: number): CastStripSnapshot {
    if (this.startedAt === null) {
      return { visible: false, stage: 0, rejected: false };
    }
    if (this.endedAt !== null && now - this.endedAt > FADE_AFTER_END_MS) {
      return { visible: false, stage: 0, rejected: false };
    }
    return {
      visible: true,
      stage: this.stage,
      rejected: this.rejected,
    };
  }
}

export const castStripState = new CastStripStateInner();
